import PropTypes from 'prop-types';
import React from 'react';

import { ContentZonesService, LocaleService } from '@sm360/widget-commons-core-services';

import PurchaseMethodsModel from 'commons-vehicle/PurchaseMethods/models/PurchaseMethodsModel.js';
import { PriceAmounts } from '@sm360/widget-commons-general';
import { CURRENCY_0, CURRENCY_2 } from 'commons-vehicle/constants/currencyFormats.js';
import { getFormattedCurrencySplit } from 'commons-vehicle/PurchaseMethods/services/PurchaseMethodsService/PurchaseMethodsService.js';

import 'commons-vehicle/Vehicle/components/PaymentV2/Financing/Financing.scss';

const Financing = ({
    children,
    purchaseMethods,
    taxCodes,
    isUsed,
    rebates,
    dealerRebates,
    electricRebate,
    displayCreditRate,
}) => {
    const term = purchaseMethods.getSelectedFinanceTerm();
    const costOfCredit = purchaseMethods.getSelectedFinanceCostOfCredit();
    const sellingPrice = purchaseMethods.getSelectedFinanceSellingPrice();

    if (!term || term.calculatedPayment <= 0) {
        return (
            <div className="financing-section tile-payment-info">
                <div className="tile-frequency is-bold">
                    {purchaseMethods.getPurchaseMethodLabels(LocaleService).verb}
                </div>
                <span className="tile-payment-info-unavailable">{LocaleService.t('NOT_AVAILABLE')}</span>
            </div>
        );
    }

    const calculatedPaymentSplit = getFormattedCurrencySplit(LocaleService.l(term.calculatedPayment, CURRENCY_2));
    const creditRate = term?.aprDetails?.creditRate || term?.creditRate;
    const aprSplit =
        !displayCreditRate || !creditRate ? term.apr.toFixed(2).split('.') : creditRate.toFixed(2).split('.');

    let includedDisclaimer;
    if (isUsed) {
        includedDisclaimer = ContentZonesService.getContentZone(
            'widget.commons',
            'disclaimer.price.included.used.finance'
        )
            ? ContentZonesService.getContentZone('widget.commons', 'disclaimer.price.included.used.finance').getValue()
            : '';
    }

    const taxe = purchaseMethods.getSelectedFinanceCalculatedPayments().calculatedPaymentTaxes
        ? purchaseMethods.getSelectedFinanceCalculatedPayments().calculatedPaymentTaxes.toFixed(2)
        : 0;

    const totalPrice = purchaseMethods.getSelectedFinanceCalculatedPayments().calculatedPayment
        ? purchaseMethods.getSelectedFinanceCalculatedPayments().calculatedPayment.toFixed(2)
        : 0;

    return (
        <div className="financing-section tile-payment-info">
            <div className="tile-frequency is-bold">
                {purchaseMethods.getPurchaseMethodLabels(LocaleService).verb}
                <span>
                    / {term.term} {LocaleService.t('TERMS.MONTH')}
                </span>
            </div>
            <div className="tile-payment">
                <div className={`tile-payment-amount is-bold is-price ${displayCreditRate && 'display-credit-rate'}`}>
                    <span className="tile-price">
                        <PriceAmounts beforeDecimal={calculatedPaymentSplit.beforeDecimal} />
                    </span>
                    <sup>
                        <PriceAmounts afterDecimal={calculatedPaymentSplit.afterDecimal} />
                    </sup>
                    <span>{purchaseMethods.getPaymentFrequencyLabels(false, LocaleService).perPeriod}</span>
                    <div className="tile-term">
                        <span className="is-small is-light">
                            + <PriceAmounts price={LocaleService.l(parseFloat(taxe), CURRENCY_2)} /> ({taxCodes}) ={' '}
                            <PriceAmounts price={LocaleService.l(parseFloat(totalPrice), CURRENCY_2)} />
                        </span>
                    </div>
                </div>

                <div className="tile-payment-rate">
                    <span className="tile-price">{aprSplit[0]}</span>
                    <sup>
                        {aprSplit[1]}% {displayCreditRate && creditRate ? 'APR' : ''}
                    </sup>
                    <span className="frequency-highlight">
                        / {term.term} {LocaleService.t('TERMS.MONTH')}
                    </span>
                </div>
                <div className="tile-payment-detail small">
                    <div className="tile-payment-detail-wrapper">
                        <span className="tile-payment-detail-note">
                            {LocaleService.t('DOWN_PAYMENTS.TOTAL_CREDIT')}
                        </span>
                        <span>
                            <PriceAmounts price={LocaleService.l(costOfCredit, CURRENCY_2)} />
                        </span>
                    </div>
                    <div className="payment-rebates">
                        {rebates}
                        {dealerRebates}
                        {electricRebate}
                    </div>
                </div>

                {includedDisclaimer && <div className="tile-payment-disclaimer-included">{includedDisclaimer}</div>}

                <div className="tile-payment-detail cashdown">
                    <div className="tile-payment-detail-wrapper">
                        <span className="tile-payment-detail-note">
                            {LocaleService.t('DOWN_PAYMENTS.CASH_DOWN_LABEL')}
                        </span>
                        <span>
                            <PriceAmounts price={LocaleService.l(term.cashDown, CURRENCY_0)} />
                        </span>
                    </div>
                </div>
                <div className="tile-payment-detail">
                    <div className="tile-payment-detail-wrapper">
                        <span className="tile-payment-detail-note">
                            {LocaleService.t('DOWN_PAYMENTS.SELLING_PRICE')}
                        </span>
                        <span>
                            *<PriceAmounts price={LocaleService.l(sellingPrice, CURRENCY_2)} />
                        </span>
                    </div>
                </div>
            </div>
            <div className="payment-rebates">{children}</div>
        </div>
    );
};

Financing.defaultProps = {
    children: null,
    isUsed: false,
    rebates: null,
    dealerRebates: null,
    electricRebate: null,
    displayCreditRate: false,
};

Financing.propTypes = {
    children: PropTypes.element,
    purchaseMethods: PropTypes.instanceOf(PurchaseMethodsModel).isRequired,
    taxCodes: PropTypes.string.isRequired,
    isUsed: PropTypes.bool,
    rebates: PropTypes.element,
    dealerRebates: PropTypes.element,
    electricRebate: PropTypes.element,
    displayCreditRate: PropTypes.bool,
};

Financing.displayName = 'Financing';

export default Financing;
